/**
 * La maquinaria HTTP compartida por los dos clientes.
 *
 * Existe porque hay DOS clientes —el de la app del personal y el del panel— y
 * comparten todo lo aburrido: cabeceras, tiempos de espera, cancelación y la
 * traducción de un error de la API a `ErrorApi`. Lo que NO comparten es la
 * lista de métodos, y eso es intencionado: que el tipo no deje llamar a
 * `verificar()` con una sesión de empleada es la misma idea que la
 * comprobación de `aud` en el JWT, pero en tiempo de compilación.
 */

import { ErrorApi, ErrorDeRed } from './errores.js';

export interface OpcionesCliente {
    /** Normalmente `/api`: el front se sirve del mismo origen. */
    base?: string;
    /** De dónde sale el JWT. Se llama en cada petición. */
    token?: () => string | null;
    /** Aviso de que la sesión ya no vale, para que la app vuelva al PIN. */
    alCaducar?: () => void;
    /** Milisegundos. Subir un trozo por una línea doméstica puede tardar. */
    timeoutMs?: number;
}

interface Peticion {
    metodo: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
    ruta: string;
    cuerpo?: unknown;
    binario?: Blob;
    timeoutMs?: number;
    señal?: AbortSignal;
}

export abstract class Http {
    private readonly base: string;
    private readonly token: () => string | null;
    private readonly alCaducar: (() => void) | undefined;
    private readonly timeoutMs: number;

    constructor(opciones: OpcionesCliente = {}) {
        this.base = opciones.base ?? '/api';
        this.token = opciones.token ?? (() => null);
        this.alCaducar = opciones.alCaducar;
        this.timeoutMs = opciones.timeoutMs ?? 120_000;
    }

    /**
     * El token de esta petición, para lo poquísimo que no puede pasar por
     * `pedir()`: la descarga del CSV, que se hace con `fetch` a pelo para
     * poder entregar el fichero como blob. Un enlace `<a href>` no manda
     * cabeceras, y el CSV lleva datos de personal.
     */
    tokenActual(): string | null {
        return this.token();
    }

    /** Atajo para los métodos que solo mandan JSON. */
    protected enviar<T>(
        metodo: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
        ruta: string,
        cuerpo?: unknown
    ): Promise<T> {
        return this.pedir<T>({ metodo, ruta, ...(cuerpo !== undefined ? { cuerpo } : {}) });
    }

    // --- El fondo del asunto --------------------------------------------

    protected async pedir<T>(p: Peticion): Promise<T> {
        const controlador = new AbortController();
        const limite = setTimeout(() => controlador.abort(), p.timeoutMs ?? this.timeoutMs);

        // Si quien llama trae su propio AbortSignal (la cola cancela subidas
        // al perder la red), se encadenan: cualquiera de los dos aborta.
        const alAbortar = (): void => controlador.abort();
        p.señal?.addEventListener('abort', alAbortar, { once: true });

        const cabeceras: Record<string, string> = {};
        const jwt = this.token();
        if (jwt !== null) {
            cabeceras['Authorization'] = `Bearer ${jwt}`;
        }

        let cuerpo: BodyInit | undefined;
        if (p.binario !== undefined) {
            cabeceras['Content-Type'] = 'application/octet-stream';
            cuerpo = p.binario;
        } else if (p.cuerpo !== undefined) {
            cabeceras['Content-Type'] = 'application/json';
            cuerpo = JSON.stringify(p.cuerpo);
        }

        let respuesta: Response;
        try {
            respuesta = await fetch(this.base + p.ruta, {
                method: p.metodo,
                headers: cabeceras,
                ...(cuerpo !== undefined ? { body: cuerpo } : {}),
                signal: controlador.signal,
                // Nada de caché: aquí se sirven asignaciones del día y
                // metadatos de evidencia.
                cache: 'no-store',
                credentials: 'omit',
            });
        } catch (e) {
            throw new ErrorDeRed(e);
        } finally {
            clearTimeout(limite);
            p.señal?.removeEventListener('abort', alAbortar);
        }

        const peticionId = respuesta.headers.get('X-Peticion-Id');
        const texto = await respuesta.text();
        let datos: unknown = null;
        if (texto !== '') {
            try {
                datos = JSON.parse(texto);
            } catch {
                datos = null;
            }
        }

        if (!respuesta.ok) {
            const obj = (datos ?? {}) as Record<string, unknown>;
            const codigo = typeof obj['error'] === 'string' ? obj['error'] : `http_${respuesta.status}`;
            const error = new ErrorApi(respuesta.status, codigo, obj, peticionId);

            // El aviso de caducidad se da una vez, aquí, para que ninguna
            // pantalla tenga que acordarse de comprobarlo.
            if (error.sesionCaducada && this.alCaducar !== undefined) {
                this.alCaducar();
            }

            throw error;
        }

        return datos as T;
    }
}
